<template>
  <div class="apply-page">
    <div class="apply-inner">

      <!-- Step indicator -->
      <div v-if="currentStep > 0 && currentStep < 6" class="step-indicator">
        <div v-for="n in 5" :key="n" class="step-dot" :class="{ active: n === currentStep, done: n < currentStep }"></div>
      </div>

      <v-progress-circular v-if="loading" indeterminate color="#1a1a1a" class="loading-spinner"></v-progress-circular>

      <!-- Step 1: Auth -->
      <div v-else-if="currentStep === 1" class="step-card">
        <p class="step-label">Step 1 of 5</p>
        <h1>Apply for the En Pointe Cohort</h1>
        <p class="step-desc">Enter your email to get started. If you've used Broche Ballet before, we'll log you in automatically.</p>

        <!-- Email -->
        <div v-if="loginStep === 'email'">
          <form @submit.prevent="onCheckEmail">
            <div class="form-group">
              <label>Email *</label>
              <input v-model="loginEmail" type="email" required class="form-input" placeholder="you@example.com" />
            </div>
            <p v-if="loginError" class="error-text">{{ loginError }}</p>
            <button type="submit" class="btn-primary" :disabled="!loginEmail || checkingEmail">
              {{ checkingEmail ? 'Checking…' : 'Continue' }}
            </button>
          </form>
        </div>

        <!-- Existing account -->
        <div v-else-if="loginStep === 'password'">
          <p class="step-note">Welcome back! Enter your password to continue.</p>
          <form @submit.prevent="onSignIn">
            <div class="form-group">
              <label>Email</label>
              <input :value="loginEmail" type="email" disabled class="form-input form-input-disabled" />
            </div>
            <div class="form-group">
              <label>Password *</label>
              <input v-model="loginPassword" type="password" required class="form-input" />
            </div>
            <p v-if="loginError" class="error-text">{{ loginError }}</p>
            <button type="submit" class="btn-primary" :disabled="!loginPassword">Log In</button>
            <p class="small-link"><a href="#" @click.prevent="onResetPassword">Forgot password?</a></p>
            <p v-if="resetSent" class="success-text">Password reset email sent!</p>
          </form>
          <p class="small-link"><a href="#" @click.prevent="loginStep = 'email'">Use a different email</a></p>
        </div>

        <!-- Already logged in -->
        <div v-else-if="loginStep === 'loggedIn'">
          <p class="step-note">You're signed in as <strong>{{ loginEmail }}</strong>.</p>
          <div v-if="existingApplicationStatus" class="info-card">
            <p v-if="existingApplicationStatus === 'draft' || existingApplicationStatus === 'pending_payment'">
              You have an in-progress application for this cohort. Click Continue to pick up where you left off.
            </p>
          </div>
          <div v-if="freeFollowUpCount > 0" class="info-card">
            <p>You have <strong>{{ freeFollowUpCount }}</strong> free re-application{{ freeFollowUpCount > 1 ? 's' : '' }} from a Foot 360 Consultation — no payment required.</p>
          </div>
          <button class="btn-primary" :disabled="savingStep" @click="proceedAsLoggedInUser">
            {{ savingStep ? 'Loading…' : 'Continue →' }}
          </button>
          <p class="small-link"><a href="#" @click.prevent="onSignOut">Use a different account</a></p>
        </div>

        <!-- New account -->
        <div v-else-if="loginStep === 'setPassword'">
          <p class="step-note">Create a password for your Broche Ballet account.</p>
          <form @submit.prevent="onSignUp">
            <div class="form-group">
              <label>Email</label>
              <input :value="loginEmail" type="email" disabled class="form-input form-input-disabled" />
            </div>
            <div class="form-group">
              <label>Create Password *</label>
              <input v-model="loginPassword" type="password" required class="form-input" />
            </div>
            <div class="form-group">
              <label>Confirm Password *</label>
              <input v-model="confirmPassword" type="password" required class="form-input" />
            </div>
            <p v-if="loginError" class="error-text">{{ loginError }}</p>
            <button type="submit" class="btn-primary" :disabled="!loginPassword || !confirmPassword">Set Password & Continue</button>
          </form>
          <p class="small-link"><a href="#" @click.prevent="loginStep = 'email'">Use a different email</a></p>
        </div>
      </div>

      <!-- Step 2: Initial Questionnaire -->
      <div v-else-if="currentStep === 2" class="step-card">
        <p class="step-label">Step 2 of 5</p>
        <h1>A Little About You</h1>

        <!-- Gate: age -->
        <div v-if="!ageConfirmed">
          <p class="step-desc">Before we dive in, let's make sure this is the right fit.</p>
          <div class="form-group">
            <label>Are you over 18 years of age? *</label>
            <div class="radio-group">
              <label class="radio-option"><input type="radio" v-model="questionnaire.over18" value="yes" /> Yes</label>
              <label class="radio-option"><input type="radio" v-model="questionnaire.over18" value="no" /> No</label>
            </div>
          </div>
          <div v-if="questionnaire.over18 === 'no'" class="ineligible-card">
            <p>We're sorry — the En Pointe Program is designed for adults 18 and older. We hope to see you when you're ready!</p>
          </div>
          <button v-else class="btn-primary" :disabled="!questionnaire.over18" @click="ageConfirmed = true">Continue</button>
        </div>

        <div v-else>
          <!-- Relationship to pointe -->
          <div class="form-group">
            <label>Which of these best describes your relationship to pointe? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in pointeRelationshipOptions" :key="opt">
                <input type="radio" v-model="questionnaire.pointeRelationship" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Info card -->
          <div v-if="questionnaire.pointeRelationship" class="info-card">
            <p>Pointework is achievable at any age for adult ballet dancers who are willing to put in the work. Our program is designed to guide you safely — wherever you're starting from.</p>
          </div>

          <!-- Intent -->
          <div v-if="questionnaire.pointeRelationship" class="form-group">
            <label>Why are you applying? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in applicationIntentOptions" :key="opt">
                <input type="radio" v-model="questionnaire.applicationIntent" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Info card: time commitment -->
          <div v-if="questionnaire.applicationIntent" class="info-card">
            <h4>What this commitment looks like:</h4>
            <ul>
              <li>Weekly video check-ins with the cohort</li>
              <li>Daily foot & ankle conditioning work</li>
              <li>Regular ballet classes (we'll help you find one!)</li>
            </ul>
          </div>

          <!-- Commitment -->
          <div v-if="questionnaire.applicationIntent" class="form-group">
            <label>Are you ready to commit to 16 weeks of pointe class? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in timeCommitmentOptions" :key="opt">
                <input type="radio" v-model="questionnaire.timeCommitmentReady" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Investment info + confirmation -->
          <div v-if="questionnaire.timeCommitmentReady === 'I am ready for a weekly commitment'" class="info-card">
            <h4>Ready to Invest in Your Pointe Journey?</h4>
            <p><strong>Application Fee:</strong> $45</p>
            <p><strong>If accepted — Cohort Fee:</strong> $400 (Broche Ballet members receive a discount based on their membership cost).</p>
            <p><strong>If not accepted:</strong> Your application fee can be applied toward a Foot 360 Consultation.</p>
          </div>

          <div v-if="questionnaire.timeCommitmentReady === 'I am ready for a weekly commitment'" class="form-group">
            <label>Are you ready to invest in your pointe future? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in readyToInvestOptions" :key="opt">
                <input type="radio" v-model="questionnaire.readyToInvest" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <div v-if="questionnaire.readyToInvest === 'Not right now'" class="ineligible-card">
            <p>No problem! Come back when the time feels right. You can join the waitlist to be notified when the next cohort opens.</p>
          </div>

          <!-- Self-assessment intro -->
          <div v-if="questionnaire.readyToInvest === 'Yes, let\'s keep going'" class="info-card">
            <h4>Quick Self-Assessment</h4>
            <p>There are 5 key criteria for pointework. Let's do a quick check to see where you stand — this helps us give you the most useful feedback.</p>
          </div>

          <!-- Criteria 1: Balance -->
          <div v-if="questionnaire.readyToInvest === 'Yes, let\'s keep going'" class="form-group">
            <label>Criteria 1 — Balance: Can you balance in 1st position on demi-pointe for 10 seconds without the barre? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in selfAssessCriteriaOptions" :key="opt">
                <input type="radio" v-model="questionnaire.balanceCriteria" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Criteria 2: Strength -->
          <div v-if="questionnaire.balanceCriteria" class="form-group">
            <label>Criteria 2 — Explosive Strength: Can you do 8 consecutive rélevé passé on each leg without the barre? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in selfAssessCriteriaOptions" :key="opt">
                <input type="radio" v-model="questionnaire.strengthCriteria" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Criteria 3: Dexterity -->
          <div v-if="questionnaire.strengthCriteria" class="form-group">
            <label>Criteria 3 — Feet Dexterity: Can you point and flex your toes independently without your ankle or your knee moving? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in dexterityCriteriaOptions" :key="opt">
                <input type="radio" v-model="questionnaire.dexterityCriteria" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Criteria 4: Ballet knowledge -->
          <div v-if="questionnaire.dexterityCriteria" class="form-group">
            <label>Criteria 4 — Ballet Knowledge: Can you complete this combination?</label>
            <div class="info-card" style="margin-bottom: 12px;">
              <p style="margin:0; font-size:14px; line-height:1.7;">
                Waltz/Balancé 4x · Tombé pas de bourrée · Tendu Derrière · Plié 4th position · Single pirouette · Land in 4th lunge · Repeat to the Left
              </p>
            </div>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in balletKnowledgeOptions" :key="opt">
                <input type="radio" v-model="questionnaire.balletKnowledgeCriteria" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Criteria 5: Ankle mobility -->
          <div v-if="questionnaire.balletKnowledgeCriteria" class="form-group">
            <label>Criteria 5 — Ankle Mobility *</label>
            <div class="info-card" style="margin-bottom: 12px;">
              <p style="margin:0; font-size:14px; line-height:1.7;">
                For each foot: (1) Measure the distance from the center of your pinky toe knuckle to the ground. (2) Measure the distance from the center of your ankle bone to the ground. Your pinky toe measurement must be ≤ your ankle bone measurement to pass.
              </p>
            </div>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in ankleMobilityOptions" :key="opt">
                <input type="radio" v-model="questionnaire.ankleMobilityCriteria" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <!-- Self-assessment result -->
          <div v-if="questionnaire.ankleMobilityCriteria" class="form-group">
            <label>How did you do on the self-assessment? *</label>
            <div class="radio-group">
              <label class="radio-option" v-for="opt in selfAssessmentResultOptions" :key="opt">
                <input type="radio" v-model="questionnaire.selfAssessmentResult" :value="opt" />
                {{ opt }}
              </label>
            </div>
          </div>

          <button
            v-if="questionnaire.selfAssessmentResult"
            class="btn-primary"
            @click="saveQuestionnaireAndAdvance"
            :disabled="savingStep"
          >
            {{ savingStep ? 'Saving…' : 'Continue' }}
          </button>
        </div>
      </div>

      <!-- Step 3: Payment -->
      <div v-else-if="currentStep === 3" class="step-card">
        <p class="step-label">Step 3 of 5</p>
        <h1>Application Fee</h1>

        <div v-if="loadingPayment" class="loading-wrap">
          <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
          <p>Checking your account…</p>
        </div>

        <div v-else>
          <!-- Free follow-up available -->
          <div v-if="freeFollowUpAvailable" class="followup-card">
            <h3>You have a free follow-up available!</h3>
            <p>
              Your previous Foot 360 Consultation included 2 free re-application reviews.
              You can use one now — no payment required.
            </p>
            <button class="btn-primary" :disabled="savingStep" @click="useFollowUp">
              {{ savingStep ? 'Processing…' : 'Use My Free Follow-up' }}
            </button>
            <p class="or-divider">— or —</p>
            <p class="small-text">You can also pay the full application fee instead.</p>
            <button class="btn-outline" @click="freeFollowUpAvailable = false">Pay Full Fee Instead</button>
          </div>

          <!-- Payment -->
          <div v-else>
            <div class="payment-summary">
              <p class="cohort-name-label">{{ cohort ? cohort.name : '' }}</p>
              <div class="price-row">
                <span>Application Fee</span>
                <span>${{ cohort ? cohort.applicationFee : '—' }}</span>
              </div>
              <div class="price-row total-row">
                <span>Total</span>
                <span>${{ cohort ? cohort.applicationFee : '—' }}</span>
              </div>
            </div>
            <p v-if="payError" class="error-text">{{ payError }}</p>
            <button class="btn-primary" :disabled="savingStep" @click="startPayment">
              {{ savingStep ? 'Redirecting to checkout…' : 'Pay & Continue' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Step 4: History & Health Questionnaire -->
      <div v-else-if="currentStep === 4" class="step-card">
        <p class="step-label">Step 4 of 5</p>
        <h1>Your Foot 360 History</h1>
        <p class="step-desc">This helps us tailor your assessment. All answers are private.</p>

        <!-- Health Screening section -->
        <div class="q-section-header">
          <h3>Health Screening</h3>
          <p class="section-note">Please answer all questions honestly. Some conditions may affect your safety during pointe work.</p>
        </div>

        <div class="form-group">
          <label>Are you all clear from injuries? *</label>
          <div class="radio-group">
            <label class="radio-option"><input type="radio" v-model="historyQ.injuryClear" value="Yes" /> Yes</label>
            <label class="radio-option"><input type="radio" v-model="historyQ.injuryClear" value="No" /> No</label>
          </div>
        </div>

        <div class="form-group">
          <label>Are you pregnant or hoping to be expecting within the duration of this cohort? *</label>
          <div class="radio-group">
            <label class="radio-option"><input type="radio" v-model="historyQ.pregnant" value="Yes" /> Yes</label>
            <label class="radio-option"><input type="radio" v-model="historyQ.pregnant" value="No" /> No</label>
          </div>
        </div>

        <div class="form-group">
          <label>Do you have known low bone density? *</label>
          <div class="radio-group">
            <label class="radio-option"><input type="radio" v-model="historyQ.lowBoneDensity" value="Yes" /> Yes</label>
            <label class="radio-option"><input type="radio" v-model="historyQ.lowBoneDensity" value="No" /> No</label>
          </div>
        </div>

        <div class="form-group">
          <label>Do you have any medical conditions that cause numbness in your toes or feet? *</label>
          <div class="radio-group">
            <label class="radio-option"><input type="radio" v-model="historyQ.toeNumbness" value="Yes" /> Yes</label>
            <label class="radio-option"><input type="radio" v-model="historyQ.toeNumbness" value="No" /> No</label>
          </div>
        </div>

        <div class="form-group">
          <label>Do you have any plates or bone fusions in your toes, feet and ankles? *</label>
          <div class="radio-group">
            <label class="radio-option"><input type="radio" v-model="historyQ.boneFusions" value="Yes" /> Yes</label>
            <label class="radio-option"><input type="radio" v-model="historyQ.boneFusions" value="No" /> No</label>
          </div>
        </div>

        <div class="form-group">
          <label>Have you taken consistent 2–3x weekly ballet classes (online or in-person) for the past 6 months? *</label>
          <div class="radio-group">
            <label class="radio-option"><input type="radio" v-model="historyQ.consistentClasses" value="Yes" /> Yes</label>
            <label class="radio-option"><input type="radio" v-model="historyQ.consistentClasses" value="No" /> No</label>
          </div>
        </div>

        <!-- Dance History section -->
        <div class="q-section-header">
          <h3>Dance History</h3>
        </div>

        <div class="form-group">
          <label>Tell us about your journey with dance so far! *</label>
          <textarea v-model="historyQ.danceJourney" class="form-input form-textarea" rows="3" placeholder="How long have you been dancing? What styles? What do you love about it?"></textarea>
        </div>

        <div class="form-group">
          <label>Have you ever been on pointe before?</label>
          <textarea v-model="historyQ.pointeHistory" class="form-input form-textarea" rows="2" placeholder="If yes, when and for how long?"></textarea>
        </div>

        <div class="form-group">
          <label>What other physical activities have you been a part of?</label>
          <textarea v-model="historyQ.otherActivities" class="form-input form-textarea" rows="2" placeholder="Sports, yoga, pilates, running, etc."></textarea>
        </div>

        <div class="form-group">
          <label>Have you ever been a runner or regularly included running? *</label>
          <div class="radio-group">
            <label class="radio-option"><input type="radio" v-model="historyQ.isRunner" value="yes" /> Yes</label>
            <label class="radio-option"><input type="radio" v-model="historyQ.isRunner" value="no" /> No</label>
          </div>
        </div>

        <div class="form-group">
          <label>What are your goals, hopes, and dreams? *</label>
          <textarea v-model="historyQ.goals" class="form-input form-textarea" rows="3" placeholder="What does going en pointe mean to you?"></textarea>
        </div>

        <!-- Body & History section -->
        <div class="q-section-header">
          <h3>Body & History</h3>
        </div>

        <div class="form-group">
          <label>Do you work in a job where you sit or stand most of the day?</label>
          <textarea v-model="historyQ.jobPosture" class="form-input form-textarea" rows="2" placeholder="Describe your typical workday posture"></textarea>
        </div>

        <div class="form-group">
          <label>What kind of footwear do you normally wear?</label>
          <textarea v-model="historyQ.normalFootwear" class="form-input form-textarea" rows="2" placeholder="Heels, flats, sneakers, barefoot at home, etc."></textarea>
        </div>

        <div class="form-group">
          <label>What is your age? *</label>
          <input v-model.number="historyQ.age" type="number" min="18" max="100" class="form-input form-input-short" />
        </div>

        <div class="form-group">
          <label>Have you had any ankle, foot, or lower leg injuries?</label>
          <textarea v-model="historyQ.injuries" class="form-input form-textarea" rows="2" placeholder="Sprains, fractures, tendinitis, etc."></textarea>
        </div>

        <div class="form-group">
          <label>If you had an injury, did you have surgery or physical therapy?</label>
          <textarea v-model="historyQ.injuryTreatment" class="form-input form-textarea" rows="2"></textarea>
        </div>

        <div class="form-group">
          <label>Do you experience any of the following?</label>
          <div class="checkbox-group">
            <label class="checkbox-option" v-for="opt in footConditionOptions" :key="opt">
              <input type="checkbox" :value="opt" v-model="historyQ.footConditions" />
              {{ opt }}
            </label>
          </div>
        </div>

        <div class="form-group">
          <label>Point your toes on your Right foot as hard as you can — describe what you feel</label>
          <textarea v-model="historyQ.rightFootPoint" class="form-input form-textarea" rows="2" placeholder="Any tightness, discomfort, or sensation to note?"></textarea>
        </div>

        <div class="form-group">
          <label>How about your Left foot?</label>
          <textarea v-model="historyQ.leftFootPoint" class="form-input form-textarea" rows="2"></textarea>
        </div>

        <!-- Resources section -->
        <div class="q-section-header">
          <h3>Your Resources</h3>
          <p class="section-note">This helps us give you the best guidance for your local area.</p>
        </div>

        <div class="form-group">
          <label>What city are you in or near? *</label>
          <input v-model="historyQ.city" type="text" class="form-input" placeholder="City, State" />
        </div>

        <div class="form-group">
          <label>Is there a studio that offers ballet classes for adults?</label>
          <textarea v-model="historyQ.localStudio" class="form-input form-textarea" rows="2" placeholder="Studio name and location, if known"></textarea>
        </div>

        <div class="form-group">
          <label>If so, do they offer pointe classes? If not, do they offer private lessons?</label>
          <textarea v-model="historyQ.pointeClassesAvailable" class="form-input form-textarea" rows="2"></textarea>
        </div>

        <div class="form-group">
          <label>Do you plan to take your pointe shoes to your in-person classes after graduating the program?</label>
          <div class="radio-group">
            <label class="radio-option" v-for="opt in pointeShoeAccessOptions" :key="opt">
              <input type="radio" v-model="historyQ.pointeShoeAccess" :value="opt" />
              {{ opt }}
            </label>
          </div>
        </div>

        <div class="form-group">
          <label>Is there a dancewear store that sells pointe shoes near you?</label>
          <textarea v-model="historyQ.pointeShoeStore" class="form-input form-textarea" rows="2"></textarea>
        </div>

        <!-- Final question -->
        <div class="q-section-header">
          <h3>Last Question</h3>
        </div>

        <div class="form-group">
          <label>Which of these scenarios best describes you?</label>
          <div class="radio-group">
            <label class="radio-option" v-for="opt in applicantScenarioOptions" :key="opt">
              <input type="radio" v-model="historyQ.applicantScenario" :value="opt" />
              {{ opt }}
            </label>
          </div>
        </div>

        <p v-if="saveError" class="error-text">{{ saveError }}</p>

        <button
          class="btn-primary"
          :disabled="!historyQValid || savingStep"
          @click="saveHistoryAndAdvance"
        >
          {{ savingStep ? 'Saving…' : 'Continue' }}
        </button>
      </div>

      <!-- Step 5: Video Upload -->
      <div v-else-if="currentStep === 5" class="step-card">
        <p class="step-label">Step 5 of 5</p>
        <h1>Upload Your Video</h1>
        <p class="step-desc">
          We need a short video of your feet and ankles to complete your assessment.
          Follow the link below to upload via Dropbox — it only takes a minute.
        </p>

        <div class="video-upload-card">
          <div class="video-icon">📹</div>
          <h3>What to record:</h3>
          <ul>
            <li>Both feet and ankles from the front</li>
            <li>A slow relevé (rising onto the balls of your feet)</li>
            <li>Pointing your feet as fully as you can</li>
            <li>Any relevant flexibility or strength you want us to see</li>
          </ul>
          <p class="upload-note">Keep it under 3 minutes. No need to be fancy — your phone is perfect.</p>
          <a
            v-if="cohort && cohort.dropboxVideoRequestUrl"
            :href="cohort.dropboxVideoRequestUrl"
            target="_blank"
            class="btn-dropbox"
          >
            Upload My Video on Dropbox →
          </a>
          <p v-else class="error-text">Video upload link not configured. Please contact hello@brocheballet.com.</p>
        </div>

        <div class="confirm-section">
          <p>Once you've uploaded your video, click the button below to complete your application.</p>
          <button
            class="btn-primary"
            :disabled="savingStep"
            @click="confirmVideoAndSubmit"
          >
            {{ savingStep ? 'Submitting…' : "I've Uploaded My Video" }}
          </button>
        </div>
      </div>

      <!-- Step 6: Confirmation -->
      <div v-else-if="currentStep === 6" class="step-card confirmation-card">
        <div class="check-icon">✓</div>
        <h1>Application Submitted!</h1>
        <p class="step-desc">
          You'll receive an email at <strong>{{ loginEmail }}</strong> when we've reviewed your video.
          This typically takes 1–2 weeks.
        </p>
        <div class="next-steps">
          <h3>While you wait…</h3>
          <ul>
            <li>Browse the <router-link to="/pointe/self-assessment">self-assessment criteria</router-link> to see what we're looking for</li>
            <li>Keep up your daily foot & ankle conditioning work</li>
            <li>Reach out at <a href="mailto:hello@brocheballet.com">hello@brocheballet.com</a> with any questions</li>
          </ul>
        </div>
        <router-link to="/pointe/dashboard" class="btn-primary">Go to My Dashboard</router-link>
      </div>

    </div>
  </div>
</template>

<script>
import { auth, db, functions } from '@/firebase'

export default {
  data () {
    return {
      currentStep: 1,
      loading: false,
      savingStep: false,
      saveError: '',

      // Auth
      loginStep: 'email',
      loginEmail: '',
      loginPassword: '',
      confirmPassword: '',
      loginError: '',
      checkingEmail: false,
      resetSent: false,

      // Application doc
      applicationId: null,
      cohort: null,

      // Step 2: questionnaire
      ageConfirmed: false,
      questionnaire: {
        over18: '',
        pointeRelationship: '',
        applicationIntent: '',
        timeCommitmentReady: '',
        readyToInvest: '',
        balanceCriteria: '',
        strengthCriteria: '',
        dexterityCriteria: '',
        balletKnowledgeCriteria: '',
        ankleMobilityCriteria: '',
        selfAssessmentResult: ''
      },

      // Step 3: payment
      loadingPayment: false,
      isMember: false,
      freeFollowUpAvailable: false,
      freeFollowUpConsultationId: null,
      freeFollowUpCount: 0,
      payError: '',

      // Auth state for already-logged-in users
      existingApplicationStatus: null,

      // Step 4: history questionnaire
      historyQ: {
        injuryClear: '',
        pregnant: '',
        lowBoneDensity: '',
        toeNumbness: '',
        boneFusions: '',
        consistentClasses: '',
        danceJourney: '',
        pointeHistory: '',
        otherActivities: '',
        isRunner: '',
        goals: '',
        jobPosture: '',
        normalFootwear: '',
        age: null,
        injuries: '',
        injuryTreatment: '',
        footConditions: [],
        rightFootPoint: '',
        leftFootPoint: '',
        city: '',
        localStudio: '',
        pointeClassesAvailable: '',
        pointeShoeAccess: '',
        pointeShoeStore: '',
        applicantScenario: ''
      },

      pointeRelationshipOptions: [
        'It is a HUGE LIFELONG dream of mine to go en pointe',
        "It is a really big dream of mine but I'm a little shy or scared to admit that",
        'I am curious about pointe and would love to give it a try',
        'I was en pointe years ago and loved it',
        'I was en pointe years ago and hated it'
      ],
      applicationIntentOptions: [
        "I'm ready to apply!!",
        "I don't think I'm ready for pointe yet, but want to apply in order to receive the feedback.",
        'I am not ready to apply yet.'
      ],
      timeCommitmentOptions: [
        'I am ready for a weekly commitment',
        "I'm not ready yet"
      ],
      readyToInvestOptions: [
        "Yes, let's keep going",
        'Not right now'
      ],
      selfAssessCriteriaOptions: [
        'Yes', 'No', 'Almost', "I'm not sure what that means"
      ],
      dexterityCriteriaOptions: [
        'Yes', 'No', 'Sometimes', "I'm not sure what that means"
      ],
      balletKnowledgeOptions: [
        'Yes',
        "Yes but I can't always land the pirouette",
        'No',
        'Almost',
        "I'm not sure what that means"
      ],
      ankleMobilityOptions: [
        'Passing on both feet',
        'Passing on one foot but not the other',
        'Not passing yet',
        "I'm not sure what that means"
      ],
      selfAssessmentResultOptions: [
        "I'm ready to apply!",
        "I'm not ready for pointe yet, but want to apply in order to receive the feedback"
      ],
      footConditionOptions: [
        'Hammer Toes', 'Bunions', 'Ingrown Toenails', 'Flat Feet', 'Achilles Pain', 'Plantar Fasciitis'
      ],
      pointeShoeAccessOptions: [
        'Yes, my studio allows people to wear pointe shoes to regular ballet technique classes',
        'Yes, my studio offers a separate adult pointe class',
        "No, I don't have access to in-person pointe classes"
      ],
      applicantScenarioOptions: [
        "This is my first time applying, and I haven't done a Foot 360 Consultation",
        "I've purchased a Foot 360 Consultation or Journey to Pointe package course",
        "I joined a past Broche Ballet beginner pointe cohort and would like to re-take it",
        "I've been approved for pointe by another ballet studio and would like to join the cohort to revisit the basics"
      ]
    }
  },
  computed: {
    finalAmount () {
      return this.cohort ? this.cohort.applicationFee : 0
    },
    historyQValid () {
      return this.historyQ.injuryClear && this.historyQ.pregnant && this.historyQ.lowBoneDensity &&
        this.historyQ.toeNumbness && this.historyQ.boneFusions && this.historyQ.consistentClasses &&
        this.historyQ.danceJourney && this.historyQ.isRunner && this.historyQ.city
    }
  },
  async created () {
    this.$store.commit('setLayout', 'pointe-layout')
    this.loading = true
    // Check if returning from Stripe success
    if (this.$route.query.payment === 'success' && this.$route.query.applicationId) {
      this.applicationId = this.$route.query.applicationId
      const user = await this.getAuthUser()
      if (user) {
        this.loginEmail = user.email
        await this.loadCohort()
        this.currentStep = 4
        this.loading = false
        return
      }
    }
    // Load open cohort
    await this.loadCohort()
    const user = await this.getAuthUser()
    if (user) {
      this.loginEmail = user.email
      // Check if there's an in-progress application
      if (this.cohort) {
        const existing = await db.collection('pointe-applications')
          .where('dancerEmail', '==', user.email.toLowerCase())
          .where('cohortId', '==', this.cohort.id)
          .limit(1)
          .get()
        if (!existing.empty) {
          const app = existing.docs[0]
          this.applicationId = app.id
          const status = app.data().status
          if (status === 'awaiting_video') {
            this.currentStep = 5
            this.loading = false
            return
          } else if (status === 'submitted' || status === 'under_review' || status === 'approved' || status === 'needs_work') {
            this.$router.push('/pointe/dashboard')
            return
          } else {
            // draft or pending_payment — show loggedIn step
            this.existingApplicationStatus = status
          }
        }
      }
      // Load free follow-up count from consultations
      const consultSnap = await db.collection('pointe-consultations')
        .where('dancerEmail', '==', user.email.toLowerCase())
        .get()
      this.freeFollowUpCount = consultSnap.docs.reduce((sum, d) => sum + (d.data().followUpsRemaining || 0), 0)
      // Show loggedIn step — don't auto-advance
      this.loginStep = 'loggedIn'
      this.currentStep = 1
    }
    this.loading = false
  },
  methods: {
    async getAuthUser () {
      return new Promise(resolve => {
        const unsub = auth.onAuthStateChanged(user => {
          unsub()
          resolve(user)
        })
      })
    },
    async loadCohort () {
      const snap = await db.collection('pointe-cohorts').where('status', '==', 'open').limit(1).get()
      this.cohort = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() }
    },
    // Auth methods
    async onCheckEmail () {
      this.checkingEmail = true
      this.loginError = ''
      try {
        const methods = await auth.fetchSignInMethodsForEmail(this.loginEmail)
        if (methods.length > 0) {
          this.loginStep = 'password'
        } else {
          this.loginStep = 'setPassword'
        }
      } catch (e) {
        this.loginError = e.message
      }
      this.checkingEmail = false
    },
    async onSignIn () {
      this.loginError = ''
      this.loading = true
      try {
        await auth.signInWithEmailAndPassword(this.loginEmail, this.loginPassword)
        await this.afterAuth()
      } catch (e) {
        this.loginError = e.message
        this.loading = false
      }
    },
    async onSignUp () {
      if (this.loginPassword !== this.confirmPassword) {
        this.loginError = 'Passwords do not match.'
        return
      }
      this.loginError = ''
      this.loading = true
      try {
        await auth.createUserWithEmailAndPassword(this.loginEmail, this.loginPassword)
        await this.afterAuth()
      } catch (e) {
        this.loginError = e.message
        this.loading = false
      }
    },
    async onResetPassword () {
      try {
        await auth.sendPasswordResetEmail(this.loginEmail)
        this.resetSent = true
      } catch (e) {
        this.loginError = e.message
      }
    },
    async afterAuth () {
      if (!this.cohort) {
        this.$router.push('/pointe')
        return
      }
      // Check for existing application
      const existing = await db.collection('pointe-applications')
        .where('dancerEmail', '==', this.loginEmail.toLowerCase())
        .where('cohortId', '==', this.cohort.id)
        .limit(1).get()
      if (!existing.empty) {
        this.applicationId = existing.docs[0].id
        const status = existing.docs[0].data().status
        if (status === 'awaiting_video') { this.currentStep = 5 } else { this.currentStep = 2 }
      } else {
        await this.createApplicationDraft()
        this.currentStep = 2
      }
      this.loading = false
    },
    async proceedAsLoggedInUser () {
      if (!this.cohort) {
        this.$router.push('/pointe')
        return
      }
      this.savingStep = true
      if (this.applicationId) {
        this.currentStep = 2
      } else {
        await this.createApplicationDraft()
        this.currentStep = 2
      }
      this.savingStep = false
    },
    async onSignOut () {
      await auth.signOut()
      this.loginStep = 'email'
      this.loginEmail = ''
      this.loginPassword = ''
      this.confirmPassword = ''
      this.applicationId = null
      this.existingApplicationStatus = null
      this.freeFollowUpCount = 0
    },
    async createApplicationDraft () {
      const user = auth.currentUser
      const ref = await db.collection('pointe-applications').add({
        cohortId: this.cohort.id,
        cohortName: this.cohort.name,
        dancerEmail: this.loginEmail.toLowerCase(),
        dancerUid: user ? user.uid : null,
        dancerName: user ? (user.displayName || this.loginEmail) : this.loginEmail,
        status: 'draft',
        paymentStatus: 'unpaid',
        isHistorical: false,
        videoConfirmed: false,
        memberDiscountApplied: false,
        criteria: {},
        overallResult: null,
        createdAt: new Date()
      })
      this.applicationId = ref.id
    },
    // Step 2
    async saveQuestionnaireAndAdvance () {
      this.savingStep = true
      await db.collection('pointe-applications').doc(this.applicationId).update({
        questionnaire: { ...this.questionnaire },
        status: 'pending_payment'
      })
      await this.checkPaymentEligibility()
      this.currentStep = 3
      this.savingStep = false
    },
    // Step 3
    async checkPaymentEligibility () {
      this.loadingPayment = true
      const email = this.loginEmail.toLowerCase()
      // Check member status
      const memberSnap = await db.collection('dancers').where('email', '==', email).limit(1).get()
      this.isMember = !memberSnap.empty

      // Check for free follow-up consultation
      const consultSnap = await db.collection('pointe-consultations')
        .where('dancerEmail', '==', email)
        .get()
      const withFollowUp = consultSnap.docs.find(d => d.data().followUpsRemaining > 0)
      if (withFollowUp) {
        this.freeFollowUpAvailable = true
        this.freeFollowUpConsultationId = withFollowUp.id
      }

      // Update member discount on application
      if (this.isMember) {
        await db.collection('pointe-applications').doc(this.applicationId).update({
          memberDiscountApplied: true
        })
      }
      this.loadingPayment = false
    },
    async useFollowUp () {
      this.savingStep = true
      this.saveError = ''
      try {
        // Deduct follow-up
        const consultRef = db.collection('pointe-consultations').doc(this.freeFollowUpConsultationId)
        const consultDoc = await consultRef.get()
        const remaining = consultDoc.data().followUpsRemaining - 1
        const used = (consultDoc.data().followUpsUsed || 0) + 1
        await consultRef.update({ followUpsRemaining: remaining, followUpsUsed: used })

        // Update application
        await db.collection('pointe-applications').doc(this.applicationId).update({
          paymentStatus: 'free_follow_up',
          status: 'awaiting_video',
          linkedConsultationId: this.freeFollowUpConsultationId
        })
        this.currentStep = 4
      } catch (e) {
        this.saveError = e.message
      }
      this.savingStep = false
    },
    async startPayment () {
      this.savingStep = true
      this.payError = ''
      try {
        const successUrl = window.location.origin + `/pointe/apply?payment=success&applicationId=${this.applicationId}`
        const cancelUrl = window.location.origin + '/pointe/apply'
        const res = await fetch('https://us-central1-broche-ballet-online.cloudfunctions.net/createPointeCheckoutSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            applicationId: this.applicationId,
            cohortId: this.cohort.id,
            amount: this.finalAmount,
            successUrl,
            cancelUrl
          })
        })
        const data = await res.json()
        if (data.url) {
          window.location.href = data.url
        } else {
          this.payError = 'Failed to start checkout. Please try again.'
        }
      } catch (e) {
        this.payError = e.message
      }
      this.savingStep = false
    },
    // Step 4
    async saveHistoryAndAdvance () {
      this.savingStep = true
      this.saveError = ''
      try {
        await db.collection('pointe-applications').doc(this.applicationId).update({
          historyQuestions: { ...this.historyQ },
          questionnaireCompleted: true
        })
        this.currentStep = 5
      } catch (e) {
        this.saveError = e.message
      }
      this.savingStep = false
    },
    // Step 5
    async confirmVideoAndSubmit () {
      this.savingStep = true
      this.saveError = ''
      try {
        await db.collection('pointe-applications').doc(this.applicationId).update({
          videoConfirmed: true,
          videoConfirmedAt: new Date(),
          status: 'submitted',
          submittedAt: new Date()
        })
        this.currentStep = 6
      } catch (e) {
        this.saveError = e.message
      }
      this.savingStep = false
    }
  }
}
</script>

<style scoped>
.apply-page {
  min-height: 80vh;
  background: #faf9f7;
  font-family: 'Fanwood Text', Georgia, serif;
  padding: 40px 24px;
}

.apply-inner {
  max-width: 580px;
  margin: 0 auto;
}

/* Step indicator */
.step-indicator {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 32px;
}

.step-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e8e5e1;
}

.step-dot.active { background: #1a1a1a; }
.step-dot.done { background: #888; }

.loading-spinner { display: block; margin: 60px auto; }

/* Step card */
.step-card {
  background: #fff;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 40px;
}

.step-label {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .1em;
  color: #999;
  margin: 0 0 8px;
}

.step-card h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  color: #1a1a1a;
  margin: 0 0 12px;
}

.step-desc { font-size: 16px; color: #666; line-height: 1.6; margin: 0 0 28px; }
.step-note { font-size: 15px; color: #555; margin: 0 0 20px; }

/* Form elements */
.form-group { margin-bottom: 20px; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 11px 14px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  border-radius: 2px;
  box-sizing: border-box;
  color: #1a1a1a;
  background: #fff;
}

.form-input:focus { outline: none; border-color: #1a1a1a; }
.form-input-disabled { background: #f9f8f6; color: #888; }
.form-input-short { max-width: 120px; }
.form-textarea { resize: vertical; }

.radio-group { display: flex; flex-direction: column; gap: 10px; }

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 15px;
  color: #1a1a1a;
  line-height: 1.4;
}

.radio-option input { margin-top: 3px; }

.checkbox-group { display: flex; flex-direction: column; gap: 8px; }

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 15px;
  color: #1a1a1a;
}

/* Info cards */
.info-card {
  background: #faf9f7;
  border-left: 3px solid #1a1a1a;
  padding: 16px 20px;
  margin-bottom: 20px;
  font-size: 15px;
  color: #555;
  line-height: 1.6;
}

.info-card h4 { font-family: 'Quattrocento', Georgia, serif; font-size: 16px; color: #1a1a1a; margin: 0 0 8px; }
.info-card ul { margin: 0; padding-left: 20px; }
.info-card li { margin-bottom: 4px; }

.ineligible-card {
  background: #fce4ec;
  padding: 16px 20px;
  border-radius: 4px;
  font-size: 15px;
  color: #c62828;
  margin-bottom: 16px;
}

/* Section headers for step 4 */
.q-section-header {
  border-top: 1px solid #e8e5e1;
  padding-top: 20px;
  margin-top: 8px;
  margin-bottom: 16px;
}

.q-section-header h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 20px;
  color: #1a1a1a;
  margin: 0 0 4px;
}

.section-note { font-size: 14px; color: #888; margin: 4px 0 0; }

/* Payment step */
.loading-wrap { text-align: center; padding: 40px 0; }
.loading-wrap p { margin-top: 12px; color: #888; }

.followup-card {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 4px;
  padding: 24px;
  text-align: center;
}

.followup-card h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 20px;
  color: #2e7d32;
  margin: 0 0 8px;
}

.followup-card p { font-size: 15px; color: #555; margin: 0 0 16px; }
.or-divider { color: #888; margin: 12px 0; }
.small-text { font-size: 13px; color: #888; margin: 0 0 8px; }

.payment-summary {
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 20px;
  background: #faf9f7;
}

.cohort-name-label {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 16px;
  color: #1a1a1a;
  margin: 0 0 12px;
  font-weight: 600;
}

.price-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0eeec;
  font-size: 15px;
}

.price-row:last-child { border-bottom: none; }

.discount-row { color: #2e7d32; }

.total-row {
  font-weight: 600;
  font-size: 17px;
  border-bottom: none;
  border-top: 1px solid #e8e5e1;
  padding-top: 10px;
  margin-top: 4px;
}

.member-note { font-size: 13px; color: #2e7d32; margin: 8px 0 0; font-style: italic; }

/* Video upload step */
.video-upload-card {
  background: #faf9f7;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 28px;
  margin-bottom: 28px;
}

.video-icon { font-size: 40px; margin-bottom: 12px; }

.video-upload-card h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  color: #1a1a1a;
  margin: 0 0 10px;
}

.video-upload-card ul { padding-left: 20px; margin: 0 0 16px; }
.video-upload-card li { margin-bottom: 6px; font-size: 15px; color: #555; }

.upload-note { font-size: 13px; color: #888; margin-bottom: 20px; }

.btn-dropbox {
  display: inline-block;
  background: #0061FE;
  color: #fff;
  padding: 12px 24px;
  border-radius: 4px;
  text-decoration: none;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
}

.btn-dropbox:hover { background: #0050d0; }

.confirm-section {
  text-align: center;
}

.confirm-section p { font-size: 15px; color: #555; margin-bottom: 16px; }

/* Confirmation */
.confirmation-card { text-align: center; }

.check-icon {
  font-size: 56px;
  width: 80px;
  height: 80px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 36px;
}

.next-steps {
  background: #faf9f7;
  border-radius: 4px;
  padding: 20px 24px;
  margin-bottom: 24px;
  text-align: left;
}

.next-steps h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 17px;
  margin: 0 0 10px;
  color: #1a1a1a;
}

.next-steps ul { padding-left: 20px; margin: 0; }
.next-steps li { margin-bottom: 6px; font-size: 15px; color: #555; }
.next-steps a { color: #1a1a1a; }

/* Buttons */
.btn-primary {
  display: inline-block;
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 13px 28px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  cursor: pointer;
  border-radius: 2px;
  text-decoration: none;
  transition: background 0.2s;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
}

.btn-primary:hover { background: #333; }
.btn-primary:disabled { opacity: 0.5; cursor: default; }

.btn-outline {
  display: inline-block;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 10px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
  text-decoration: none;
}

.small-link { text-align: center; margin-top: 12px; }
.small-link a { font-size: 13px; color: #888; }

.success-text { color: #2e7d32; font-size: 14px; text-align: center; }
.error-text { color: #c00; font-size: 14px; margin-bottom: 12px; }

@media (max-width: 640px) {
  .step-card { padding: 24px 20px; }
}
</style>
